import {
  Anchor,
  Badge,
  Group,
  Image,
  Loader,
  NativeSelect,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useState } from "react";

import { useListBrands } from "../../api/generated/endpoints/teams/teams";
import type { Job } from "../../api/generated/model";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { useStreamStatus } from "../../platform/stream-status";
import { formatDate, formatDateTime } from "../../ui/format";
import { typeLabel } from "../../platform/studio/draft";
import { useJobList } from "../../platform/jobs/job-list";
import { statusLook } from "../../platform/jobs/detail/job-status";
import { creditsText } from "../../ui/credits";
import { LoadAhead, morePages } from "../../ui/LoadAhead";

const ROUTE = "/signed-in/teams/$teamId/jobs";
const started = formatDateTime;

/** The job's first delivered image, small, beside its link; gone if its URL does not load. */
function JobThumbnail({ job }: { job: Job }) {
  const [failedSrc, setFailedSrc] = useState<string | undefined>(undefined);
  const url = job.outputs.find(
    (output) => output.url !== null && output.mime?.startsWith("image/") === true,
  )?.url;
  if (url === undefined || url === null || url === failedSrc) return null;
  // Decorative: the link next to it names the job.
  const failed = () => setFailedSrc(url);
  return <Image src={url} alt="" w={48} h={48} fit="cover" radius="sm" onError={failed} />;
}

/**
 * The team's jobs, newest first (G4), every job a member can read (D6). The filters live in the
 * URL, so the brand nav can open a brand's jobs and a view survives a reload.
 */
export function JobListScreen() {
  const { teamId } = useParams({ from: ROUTE });
  const search = useSearch({ from: ROUTE });
  const navigate = useNavigate({ from: "/teams/$teamId/jobs" });
  const brands = useListBrands(teamId);
  const status = search.status ?? "all";
  const jobs = useJobList(teamId, { status, brandId: search.brand }, useStreamStatus());
  const setSearch = (patch: Partial<typeof search>) =>
    void navigate({ search: (current) => ({ ...current, ...patch }), replace: true });
  const items = jobs.data?.pages.flatMap((page) => page.items) ?? [];
  const brandName = new Map((brands.data ?? []).map((brand) => [brand.id, brand.name]));

  return (
    <Stack gap="lg">
      <Title order={1}>Jobs</Title>
      <Group align="flex-end" gap="sm">
        <SegmentedControl
          aria-label="Show"
          data={[
            { value: "all", label: "All" },
            { value: "active", label: "Running" },
          ]}
          value={status}
          onChange={(value) => setSearch({ status: value === "active" ? "active" : undefined })}
        />
        <NativeSelect
          // Not "Brand": the team frame's brand switcher already has that name on this page.
          label="Filter by brand"
          data={[
            { value: "", label: "All brands" },
            ...(brands.data ?? []).map((brand) => ({ value: brand.id, label: brand.name })),
          ]}
          value={search.brand ?? ""}
          onChange={(event) => setSearch({ brand: event.currentTarget.value || undefined })}
        />
      </Group>
      {jobs.isPending ? <Loader size="sm" aria-label="Loading jobs" /> : null}
      {jobs.isError ? <ErrorAlert error={jobs.error} /> : null}
      {jobs.isSuccess && items.length === 0 ? (
        <Text c="var(--cai-color-text-muted)">
          {status === "active" ? "No job is running." : "No jobs yet."}
        </Text>
      ) : null}
      {items.length === 0 ? null : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Job</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Brand</Table.Th>
              <Table.Th>Price</Table.Th>
              <Table.Th>Started</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((job) => {
              const look = statusLook(job.status);
              return (
                <Table.Tr key={job.id}>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <JobThumbnail job={job} />
                      <Stack gap={0}>
                        <Anchor
                          // Every row's link would read "Simple"; the start time tells
                          // them apart for a screen reader's list of links.
                          aria-label={`${typeLabel(job.type)}, started ${started(job.created_at)}`}
                          renderRoot={(props) => (
                            <Link
                              {...props}
                              to="/teams/$teamId/jobs/$jobId"
                              params={{ teamId, jobId: job.id }}
                            />
                          )}
                        >
                          {typeLabel(job.type)}
                        </Anchor>
                        <Text size="xs" c="var(--cai-color-text-muted)" lineClamp={1}>
                          {job.params?.prompt ?? job.model ?? ""}
                        </Text>
                      </Stack>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={look.color} variant="light">
                      {look.label}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {job.brand_id === null ? "" : (brandName.get(job.brand_id) ?? "")}
                  </Table.Td>
                  <Table.Td>{creditsText(job.quote)}</Table.Td>
                  <Table.Td>{formatDate(job.created_at)}</Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}
      {/* The shared list's rule (live has no job list): the next page as the end shows, no button. */}
      <LoadAhead
        more={morePages(jobs)}
        loaded={jobs.data?.pages.reduce((count, page) => count + page.items.length, 0) ?? 0}
        label="Loading more jobs"
      />
    </Stack>
  );
}
